/*
  Inline quick-view drop-down.

  Clicking the card "+" ([data-quickview]) inserts a full-width row into the
  product grid, directly below the clicked card's row, and fetches that product's
  `?view=quickview` markup into it (Revolve style) instead of opening a modal.

  Loaded together with component-quick-view.css by sections whose quick_add
  setting is 'add_button'. Strings come from window.quickViewStrings /
  window.variantStrings and routes from window.routes — all defined in
  layout/theme.liquid.
*/
(function () {
  'use strict';

  var strings = window.quickViewStrings || {};
  var LOADING = strings.loading || 'Loading';
  var CLOSE = strings.close || 'Close';
  var ERROR = strings.error || 'Something went wrong. Please try again.';

  // Breathing room kept below the sticky header and above the viewport bottom
  // when the panel is scrolled into view.
  var QV_GUTTER = 16;
  // Must stay in step with the max-height transition in component-quick-view.css.
  var QV_OPEN_MS = 550;

  var currentPanel = null;
  var currentTrigger = null;
  var settleTimer = null;
  var qvCache = {}; // handle → fetched quick-view HTML (no refetch on reopen)

  /* ---------------------------------------------------------------- panel */

  // All cards sharing the clicked card's top offset are on the same visual row.
  // Return the last one so the panel drops below the whole row.
  function lastItemInRow(item) {
    var top = item.offsetTop;
    var last = item;
    Array.prototype.forEach.call(item.parentElement.children, function (el) {
      if (
        el.classList.contains('grid__item') &&
        !el.classList.contains('quickview-inline') &&
        Math.abs(el.offsetTop - top) < 2
      ) {
        last = el;
      }
    });
    return last;
  }

  // How much of the top of the viewport the sticky header covers. 0 once a
  // non-sticky header has scrolled away, so we never over-correct on those.
  function headerOffset() {
    var header = document.querySelector('.section-header');
    if (!header || header.classList.contains('shopify-section-header-hidden')) return 0;
    var rect = header.getBoundingClientRect();
    return Math.max(0, Math.min(rect.bottom, rect.height));
  }

  // Full height of the content — the panel animates its max-height to this. The
  // wrap itself is never height-constrained (the cap lives on the panel), so its
  // own box is the real target; measured rather than scrollHeight to include the
  // top/bottom borders and round subpixels up instead of clipping them.
  function contentHeight(panel) {
    var wrap = panel.querySelector('.quickview-inline__wrap');
    return wrap ? Math.ceil(wrap.getBoundingClientRect().height) : 0;
  }

  // Once the open animation is done, drop the max-height cap entirely. Two wins:
  // content taller than the cap is never clipped, and later height changes
  // (image loads, variant switches) resize the panel naturally instead of
  // replaying the half-second transition from a stale target.
  function settle(panel) {
    if (currentPanel !== panel) return;
    panel.classList.add('is-settled');
    panel.style.maxHeight = 'none';
    // Second pass, now that the page is at full height. On a last-row card the
    // document was still growing during the first reveal, so the browser clamped
    // that smooth scroll short of the target. A no-op if nothing is cut off.
    revealPanel(panel, contentHeight(panel));
  }

  function scheduleSettle(panel) {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      settle(panel);
    }, QV_OPEN_MS + 70);
  }

  // No-op unless `panel` is still the open one, so late callbacks (image loads,
  // variant changes) can call this without guarding themselves. Also a no-op
  // after settle(), where the panel sizes itself.
  function expand(panel) {
    if (currentPanel !== panel || panel.classList.contains('is-settled')) return;
    panel.style.maxHeight = contentHeight(panel) + 'px';
  }

  // Scroll the opened panel fully into view *below* the sticky header. Replaces
  // scrollIntoView({block:'nearest'}), which bottom-aligned any panel taller
  // than the free space and so pushed the product title up under the header.
  // `height` is the panel's final height — passed in because the max-height
  // transition is still running, so the live rect would measure short.
  function revealPanel(panel, height) {
    var headroom = headerOffset() + QV_GUTTER;
    var top = panel.getBoundingClientRect().top;
    var bottom = top + height;
    var target;

    if (top < headroom) {
      // Panel starts underneath the header — pull it down into the clear.
      target = window.scrollY + top - headroom;
    } else if (bottom > window.innerHeight - QV_GUTTER) {
      // Bring the bottom into view, but never at the top's expense: a panel
      // taller than the free space is top-aligned so it reads from the start.
      target = Math.min(
        window.scrollY + bottom - window.innerHeight + QV_GUTTER,
        window.scrollY + top - headroom
      );
    } else {
      return; // already fully on screen
    }

    window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }

  function closeInline(immediate) {
    if (!currentPanel) return;
    var panel = currentPanel;
    currentPanel = null;
    currentTrigger = null;
    clearTimeout(settleTimer);

    if (immediate) {
      panel.remove();
      return;
    }

    var removed = false;
    function remove() {
      if (removed) return;
      removed = true;
      panel.remove();
    }

    // Pin the rendered height first: after settle() max-height is 'none', and a
    // transition from 'none' to 0 does not animate.
    panel.style.maxHeight = panel.offsetHeight + 'px';
    panel.classList.remove('is-settled', 'is-open');
    requestAnimationFrame(function () {
      panel.style.maxHeight = '0px';
    });
    panel.addEventListener('transitionend', function handler(e) {
      if (e.propertyName !== 'max-height') return;
      panel.removeEventListener('transitionend', handler);
      remove();
    });
    // Fallback: transitionend never fires under prefers-reduced-motion, so the
    // panel would otherwise stay in the DOM at zero height.
    setTimeout(remove, 600);
  }

  /* --------------------------------------------------------------- images */

  // Show the already-cached card image on the first slide instantly, then
  // silently swap in the high-res quick-view image once it downloads. The
  // quick-view requests width:800, a size the card's srcset does not carry, so
  // the slide would otherwise sit blank for a moment.
  function primeFirstImage(root, panel, src) {
    if (!src) return;
    var firstImg = root.querySelector('.qv-carousel__slide img');
    if (!firstImg) return;
    // Reopening the same product: hi-res already cached, nothing to do.
    if (firstImg.complete && firstImg.naturalWidth > 0) return;

    var hiSrcset = firstImg.getAttribute('srcset') || '';
    var hiSizes = firstImg.getAttribute('sizes') || '';
    var hiSrc = firstImg.getAttribute('src') || '';

    // Paint the cached card image immediately.
    firstImg.removeAttribute('srcset');
    firstImg.removeAttribute('sizes');
    firstImg.src = src;

    // Preload the real image off-screen; when it is ready, restore srcset so the
    // browser serves the crisp version straight from cache.
    var pre = new Image();
    pre.onload = function () {
      if (hiSizes) firstImg.setAttribute('sizes', hiSizes);
      if (hiSrcset) firstImg.setAttribute('srcset', hiSrcset);
      if (hiSrc) firstImg.src = hiSrc;
      expand(panel);
    };
    if (hiSizes) pre.sizes = hiSizes;
    if (hiSrcset) pre.srcset = hiSrcset;
    pre.src = hiSrc;
  }

  // Drives the image carousel. The markup comes from templates/product.quickview
  // — a <script> injected via innerHTML would not run, so it is wired up here.
  function initCarousel(root, panel) {
    var carousel = root.querySelector('[data-qv-carousel]');
    if (!carousel) return;

    var track = carousel.querySelector('.qv-carousel__track');
    var count = carousel.querySelectorAll('.qv-carousel__slide').length;
    var dots = carousel.querySelectorAll('.qv-carousel__dot');
    var index = 0;
    if (!track || !count) return;

    function go(i) {
      index = (i + count) % count;
      track.style.transform = 'translateX(' + -index * 100 + '%)';
      dots.forEach(function (dot, di) {
        dot.classList.toggle('is-active', di === index);
      });
    }

    var prev = carousel.querySelector('.qv-carousel__prev');
    var next = carousel.querySelector('.qv-carousel__next');
    if (prev) {
      prev.addEventListener('click', function () {
        go(index - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        go(index + 1);
      });
    }
    dots.forEach(function (dot, di) {
      dot.addEventListener('click', function () {
        go(di);
      });
    });

    // Late-loading images change the panel height.
    carousel.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', function () {
        expand(panel);
      });
    });

    go(0);
  }

  /* ----------------------------------------------------------------- form */

  // Variant boxes → live price, quantity stepper, add-to-cart and buy-now.
  function initQuickviewForm(root, panel) {
    var form = root.querySelector('[data-qv-root]');
    if (!form) return;

    var dataEl = form.querySelector('[data-qv-variant-data]');
    var variants = [];
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      variants = [];
    }

    var priceEl = form.querySelector('[data-qv-price]');
    var idInput = form.querySelector('[data-qv-id]');
    var addBtn = form.querySelector('[data-qv-add]');
    var buyBtn = form.querySelector('[data-qv-buy]');
    var groups = form.querySelectorAll('.qv-variant-group');
    var cartUrl = form.getAttribute('data-cart-url') || '/cart';

    /* quantity (− / input / +), clamped to a minimum of 1 */

    var qtyInput = form.querySelector('[data-qv-qty]');

    function getQty() {
      var n = parseInt(qtyInput && qtyInput.value, 10);
      return isNaN(n) || n < 1 ? 1 : n;
    }
    function setQty(n) {
      if (qtyInput) qtyInput.value = n < 1 ? 1 : n;
    }

    var qtyMinus = form.querySelector('[data-qv-qty-minus]');
    var qtyPlus = form.querySelector('[data-qv-qty-plus]');
    if (qtyMinus) {
      qtyMinus.addEventListener('click', function () {
        setQty(getQty() - 1);
      });
    }
    if (qtyPlus) {
      qtyPlus.addEventListener('click', function () {
        setQty(getQty() + 1);
      });
    }
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        setQty(getQty());
      });
    }

    /* variant selection */

    function selectedOptions() {
      var out = [];
      groups.forEach(function (group) {
        var checked = group.querySelector('.qv-variant-input:checked');
        if (!checked) return;
        out[parseInt(group.getAttribute('data-option-position'), 10) - 1] = checked.value;
        var label = group.querySelector('[data-qv-selected-value]');
        if (label) label.textContent = checked.value;
      });
      return out;
    }

    function matchVariant(sel) {
      return variants.find(function (v) {
        return (
          v.options.length === sel.length &&
          v.options.every(function (o, i) {
            return o === sel[i];
          })
        );
      });
    }

    function renderPrice(v) {
      if (!priceEl || !v) return;
      var html =
        '<span class="qv-price__current' +
        (v.compare ? ' qv-price__current--sale' : '') +
        '">' +
        v.price +
        '</span>';
      if (v.compare) html += '<s class="qv-price__compare">' + v.compare + '</s>';
      priceEl.innerHTML = html;
    }

    function setSoldOut(soldOut) {
      var labels = window.variantStrings || {};
      if (addBtn) {
        addBtn.disabled = soldOut;
        addBtn.textContent = soldOut ? labels.soldOut || 'Sold out' : labels.addToCart || 'Add to cart';
      }
      if (buyBtn) buyBtn.disabled = soldOut;
    }

    function update() {
      // No option groups → single-variant product; fall back to the preset id.
      var v = groups.length
        ? matchVariant(selectedOptions())
        : variants.find(function (x) {
            return String(x.id) === String(idInput && idInput.value);
          }) || variants[0];

      if (v) {
        if (idInput) idInput.value = v.id;
        renderPrice(v);
      }
      setSoldOut(!v || !v.available);
      expand(panel);
    }

    form.querySelectorAll('.qv-variant-input').forEach(function (input) {
      input.addEventListener('change', update);
    });

    /* cart */

    function addToCart(redirectToCheckout) {
      if (!idInput || !idInput.value) return;
      var busyBtn = redirectToCheckout ? buyBtn : addBtn;
      if (busyBtn) busyBtn.setAttribute('aria-busy', 'true');

      var body = { items: [{ id: parseInt(idInput.value, 10), quantity: getQty() }] };
      var drawer = document.querySelector('cart-drawer');
      var useDrawer = !redirectToCheckout && drawer && typeof drawer.getSectionsToRender === 'function';

      if (useDrawer) {
        body.sections = drawer.getSectionsToRender().map(function (s) {
          return s.id;
        });
        body.sections_url = window.location.pathname;
      }

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (res) {
          if (res && res.status) {
            // Shopify returned an error object (e.g. the variant went out of stock).
            if (busyBtn) busyBtn.removeAttribute('aria-busy');
            return;
          }
          if (redirectToCheckout) {
            window.location.href = '/checkout';
            return;
          }
          if (useDrawer && res.sections) {
            closeInline(true);
            // When the cart was empty the <cart-drawer> element still carries
            // `is-empty`, whose CSS hides the header and collapses the items
            // area, so the drawer would render blank. renderContents only swaps
            // #CartDrawer's inner HTML, not this class (same as product-form.js).
            drawer.classList.remove('is-empty');
            drawer.renderContents(res);
          } else {
            window.location.href = cartUrl;
          }
        })
        .catch(function () {
          if (busyBtn) busyBtn.removeAttribute('aria-busy');
          window.location.href = cartUrl;
        });
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        addToCart(false);
      });
    }
    if (buyBtn) {
      buyBtn.addEventListener('click', function () {
        addToCart(true);
      });
    }

    update();
  }

  // The quick-view HTML is injected after the wishlist app has scanned the page,
  // so nudge it to render the heart on the newly added .wishlistIcon.
  function refreshWishlist() {
    try {
      var wk = window.WishlistKing;
      if (!wk) return; // its MutationObserver will pick the element up on its own
      if (typeof wk.reloadButtons === 'function') wk.reloadButtons();
      else if (typeof wk.renderButtons === 'function') wk.renderButtons();
      else if (wk.eventBus && typeof wk.eventBus.emit === 'function') wk.eventBus.emit('buttons:render');
    } catch (e) {
      /* wishlist app absent or its API changed — the heart is non-critical */
    }
  }

  /* ------------------------------------------------------------------ open */

  // Cached per product so reopening the same card is instant. Caveat: a reopened
  // panel shows price/stock as of the first open until the page is reloaded.
  function fetchQuickView(handle) {
    if (qvCache[handle]) return Promise.resolve(qvCache[handle]);
    return fetch('/products/' + handle + '?view=quickview')
      .then(function (res) {
        if (!res.ok) throw new Error(res.status);
        return res.text();
      })
      .then(function (html) {
        qvCache[handle] = html;
        return html;
      });
  }

  function openInline(trigger) {
    var item = trigger.closest('.grid__item');
    if (!item || !item.parentElement) return;

    // Clicking the same "+" again toggles the panel shut.
    if (currentTrigger === trigger) {
      closeInline();
      return;
    }
    // Swapping panels: yanking the open one out shifts the whole grid, and if it
    // sat above this card the page lurches upward. Measure the clicked card
    // before and after, then absorb the difference so it stays put on screen.
    var hadPanel = !!currentPanel;
    var anchorTop = hadPanel ? item.getBoundingClientRect().top : 0;
    closeInline(true);
    if (hadPanel) {
      var drift = item.getBoundingClientRect().top - anchorTop;
      if (drift) window.scrollBy(0, drift);
    }

    // The clicked card's image is already downloaded. Reuse it as an instant
    // placeholder so the left column isn't blank while the quick-view HTML
    // fetches and its high-res image downloads.
    var cardImgEl = item.querySelector('.card__media img') || item.querySelector('img');
    var cardImgSrc = cardImgEl ? cardImgEl.currentSrc || cardImgEl.src || '' : '';

    var loader =
      '<div class="qv-loader" role="status" aria-label="' + LOADING + '"><span class="qv-spinner"></span></div>';
    var skeleton = cardImgSrc
      ? '<div class="quickview-left"><img class="qv-skeleton-img" src="' +
        cardImgSrc +
        '" alt="" aria-hidden="true"></div>' +
        loader
      : '<div class="quickview-loading" role="status"><span class="qv-spinner"></span><span>' +
        LOADING +
        '</span></div>';

    var panel = document.createElement('li');
    panel.className = 'grid__item quickview-inline';
    panel.innerHTML =
      '<div class="quickview-inline__wrap">' +
      '<button type="button" class="quickview-close" aria-label="' +
      CLOSE +
      '">&times;</button>' +
      '<div class="quickview-content quickview-inline__content">' +
      skeleton +
      '</div>' +
      '</div>';

    lastItemInRow(item).after(panel);
    currentPanel = panel;
    currentTrigger = trigger;

    panel.style.maxHeight = '0px';
    requestAnimationFrame(function () {
      panel.classList.add('is-open');
      expand(panel);
    });

    var content = panel.querySelector('.quickview-inline__content');

    fetchQuickView(trigger.dataset.productHandle)
      .then(function (html) {
        if (currentPanel !== panel) return; // closed or superseded while fetching
        content.innerHTML = html;
        primeFirstImage(content, panel, cardImgSrc);
        initCarousel(content, panel);
        initQuickviewForm(content, panel);
        refreshWishlist();
        expand(panel);
        // Scroll and grow together, using the final height rather than the
        // mid-transition one, so the whole thing reads as a single motion.
        revealPanel(panel, contentHeight(panel));
        scheduleSettle(panel);
      })
      .catch(function () {
        if (currentPanel !== panel) return;
        content.innerHTML = '<div class="quickview-loading">' + ERROR + '</div>';
        expand(panel);
        scheduleSettle(panel);
      });
  }

  /* --------------------------------------------------------------- events */

  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;

    var trigger = e.target.closest('[data-quickview]');
    if (trigger) {
      // The "+" must open the quick-view, never submit or navigate.
      e.preventDefault();
      e.stopPropagation();
      if (trigger.dataset.productHandle) openInline(trigger);
      return;
    }
    if (e.target.closest('.quickview-close')) closeInline();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeInline();
  });

  // Row positions change on resize, so the panel would end up in the wrong place.
  window.addEventListener('resize', function () {
    closeInline(true);
  });
})();